import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Form, Radio, Select } from 'antd';
import { CALC_KINDS, CATEGORIES, INTEREST_FILTERS, STATUSES } from './format';
import { clearedParams } from './listParams';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** The months in the address (`9`, or `9,10` read back as `['9', '10']`) as numbers 1–12. */
const monthsOf = (value) =>
  [value]
    .flat()
    .flatMap((v) => String(v ?? '').split(','))
    .map(Number)
    .filter((m) => Number.isInteger(m) && m >= 1 && m <= 12);

/**
 * The „Filters" side panel of a tax returns tab, the way the app's other
 * lists have it: the filters are applied together with „Submit" and land in
 * the address; „Reset" drops them all.
 *
 * @param {string} tab - which tab's filters to offer (see `TAB_FILTERS`)
 * @param {boolean} showStatus - the payment status, only where statements are imported
 */
export default function TaxFilterForm({ tab, projects, queryParams, setQueryParams, onClose, showStatus = true }) {
  const { t } = useTranslation(['taxReturns', 'forms', 'tables']);
  const [form] = Form.useForm();

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y + 1, y, y - 1, y - 2];
  }, []);

  const values = useMemo(
    () => ({
      project: queryParams.project || undefined,
      year: queryParams.year ? Number(queryParams.year) : undefined,
      // Returns: several months at once; calculations: one.
      month: tab === 'returns' ? monthsOf(queryParams.month) : monthsOf(queryParams.month)[0],
      // The filing date unless the due date is asked for by name, as the server reads it.
      monthBy: queryParams.monthBy === 'due' ? 'due' : 'filed',
      status: queryParams.status || undefined,
      interest: queryParams.interest || undefined,
      category: queryParams.category || undefined,
      type: queryParams.type || undefined,
      kind: queryParams.kind || undefined,
      inactive: ['true', true].includes(queryParams.inactive),
      dueSoon: ['true', true].includes(queryParams.dueSoon),
    }),
    [queryParams, tab]
  );

  useEffect(() => form.setFieldsValue(values), [form, values]);

  const onFinish = (v) => {
    const next = { offset: 1 };
    for (const [k, value] of Object.entries(v)) {
      next[k] = value === false || value === null || value === '' ? undefined : value;
    }
    // Several months go to the address as `9,10`, in calendar order.
    if (Array.isArray(next.month)) {
      next.month = next.month.length ? [...next.month].sort((a, b) => a - b).join(',') : undefined;
    }
    // The filing date is the default and is left out of the address.
    if (next.monthBy !== 'due') next.monthBy = undefined;
    // „Due soon" and a payment status are two ways of picking rows; one at a time.
    if (next.status) next.dueSoon = undefined;
    setQueryParams(next);
    onClose();
  };

  const reset = () => {
    // The panel's filters only; the search and sorts in the column headers stay.
    const panel = Object.fromEntries(Object.keys(clearedParams(tab)).filter((k) => k !== 'search' && !k.startsWith('sort')).map((k) => [k, undefined]));
    setQueryParams({ ...panel, offset: 1 });
    form.resetFields();
    onClose();
  };

  const projectOptions = [
    ...projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` })),
    { value: 'none', label: t('filters.noProject') },
  ];
  const statusLabel = (s) => t(`status.${s}`);
  const calcStatuses = ['draft', 'done'];

  return (
    <Form form={form} layout="vertical" initialValues={values} onFinish={onFinish} className="form p-4">
      <Form.Item name="project" label={t('fields.project')}>
        <Select allowClear showSearch optionFilterProp="label" options={projectOptions} placeholder={t('filters.project')} />
      </Form.Item>

      {(tab === 'returns' || tab === 'calculations') && (
        <>
          <Form.Item name="year" label={t('filters.year')}>
            <Select allowClear options={years.map((y) => ({ value: y, label: y }))} placeholder={t('filters.year')} />
          </Form.Item>
          <Form.Item name="month" label={t('filters.month')}>
            <Select
              allowClear
              mode={tab === 'returns' ? 'multiple' : undefined}
              options={MONTHS.map((m) => ({ value: m, label: m }))}
              placeholder={t('filters.month')}
            />
          </Form.Item>
          {tab === 'returns' && (
            <Form.Item name="monthBy" label={t('filters.monthBy')} extra={t('filters.monthByHint')}>
              <Radio.Group>
                <Radio value="filed">{t('filters.monthByFiled')}</Radio>
                <Radio value="due">{t('filters.monthByDue')}</Radio>
              </Radio.Group>
            </Form.Item>
          )}
        </>
      )}

      {tab === 'returns' && (
        <>
          {showStatus && (
            <Form.Item name="status" label={t('filters.status')}>
              <Select
                allowClear
                options={STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))}
                placeholder={t('filters.status')}
              />
            </Form.Item>
          )}
          {showStatus && (
            <Form.Item name="interest" label={t('interest.column')}>
              <Select
                allowClear
                options={INTEREST_FILTERS.map((v) => ({ value: v, label: t(`interest.filter.${v}`) }))}
                placeholder={t('interest.column')}
              />
            </Form.Item>
          )}
          <Form.Item name="category" label={t('filters.category')}>
            <Select
              allowClear
              options={[
                ...CATEGORIES.map((c) => ({ value: c, label: t(`category.${c}`) })),
                { value: 'none', label: t('filters.noCategory') },
              ]}
              placeholder={t('filters.category')}
            />
          </Form.Item>
          <Form.Item name="type" label={t('filters.type')}>
            <Select allowClear options={['PPP-PD', 'PDPO'].map((v) => ({ value: v, label: v }))} placeholder={t('filters.type')} />
          </Form.Item>
          <Form.Item name="dueSoon" valuePropName="checked">
            <Checkbox>{t('dueSoon.card')}</Checkbox>
          </Form.Item>
        </>
      )}

      {tab === 'calculations' && (
        <>
          <Form.Item name="kind" label={t('calc.fields.kind')}>
            <Select
              allowClear
              options={CALC_KINDS.map((k) => ({ value: k, label: t(`calc.kinds.${k}`) }))}
              placeholder={t('calc.fields.kind')}
            />
          </Form.Item>
          <Form.Item name="status" label={t('fields.status')}>
            <Select
              allowClear
              options={calcStatuses.map((s) => ({ value: s, label: t(`calc.status.${s}`) }))}
              placeholder={t('fields.status')}
            />
          </Form.Item>
        </>
      )}

      {tab === 'people' && (
        <Form.Item name="inactive" valuePropName="checked">
          <Checkbox>{t('people.showInactive')}</Checkbox>
        </Form.Item>
      )}

      <div className="form-footer form-footer--sticky">
        <div className="flex gap-2">
          <Button type="default" shape="round" className="btn-default btn-default--hover" onClick={reset}>
            {t('reset', { ns: 'forms' })}
          </Button>
          <Button type="primary" shape="round" htmlType="submit" className="btn-primary">
            {t('submit', { ns: 'forms' })}
          </Button>
        </div>
      </div>
    </Form>
  );
}
